import SaveIcon from "@mui/icons-material/Save";
import {
  Alert,
  Box,
  Card,
  Dialog,
  DialogContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Typography
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useConfig } from "../../contexts/ConfigContext";
import { useSpinner } from '../../contexts/SpinnerContext';
import { useUser } from '../../contexts/UserContext';
import {
  fetchMediaItemsByIds as fetchMediaItemsService,
  updateMediaItem
} from "../../services/mediaEditService";
import { FileProgress, FileProgressStage, MediaItemDetailed, UploadPhase } from "../../types";
import { enqueueUniqueSnackbar } from "../../utilities/snackbarUtils";
import { CMButton } from "../common/CMButton";
import ToggleButton from "../common/ToggleButton";
import PreviewDialog from "./PreviewDialog";
import SingleItemPreview from "./SingleItemPreview";
import { UploadProgressBars } from "./UploadProgressBars";
import UploadTableContainer from "./UploadTableContainer";

// Feature flags (set to false to disable features)
const SHOW_RELATIONSHIP_PREVIEW_DIALOG = true; // Set to true to enable the preview dialog

interface BulkEditPageProps {
  initialItems?: MediaItemDetailed[];
  itemIds?: string[];
  onSaveComplete?: () => void;
  onCancel?: () => void;
}

const BulkEditPage: React.FC<BulkEditPageProps> = ({
  initialItems = [],
  itemIds = [],
  onSaveComplete,
  onCancel,
}) => {
  const config = useConfig();
  const { hasWritePermissions } = useUser();
  const [mediaItems, setMediaItems] = useState<MediaItemDetailed[]>(initialItems);
  const [selectedItemId, setSelectedItemId] = useState<string>();
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState<MediaItemDetailed | null>(null);
  const [isPreviewEnabled, setIsPreviewEnabled] = useState(true);
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [showDropZone, setShowDropZone] = useState(true);
  const [saveInProgress, setSaveInProgress] = useState(false);
  const [saveComplete, setSaveComplete] = useState(false);
  const [errorDetails, setErrorDetails] = useState<{
    message: string;
    failedItems?: Array<{ id: string; error: string }>;
  } | null>(null);

  const { startLoading, stopLoading } = useSpinner();

  // Add state for tracking changes
  const [changedItems, setChangedItems] = useState<Set<string>>(new Set());
  const [initialItemState, setInitialItemState] = useState<Record<string, MediaItemDetailed>>({});
  
  // Add state for tracking save progress
  const [saveProgress, setSaveProgress] = useState<Record<string, FileProgress>>({});
  const [currentPhase, setCurrentPhase] = useState<UploadPhase>("importing");
  
  // New state for showing diff preview
  const [showDiffPreview, setShowDiffPreview] = useState(false);
  const [itemsToSave, setItemsToSave] = useState<MediaItemDetailed[]>([]);
  
  // State for item limit error
  const [itemLimitError, setItemLimitError] = useState(false);
  
  // Maximum items allowed in bulk edit
  const MAX_BULK_EDIT_ITEMS = 100;

  // If initial items are provided after component mount, update state
  useEffect(() => {
    if (initialItems.length > 0) {
      // Check if we exceed the bulk edit limit
      if (initialItems.length > MAX_BULK_EDIT_ITEMS) {
        setItemLimitError(true);
        setMediaItems([]);
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `Cannot bulk edit more than ${MAX_BULK_EDIT_ITEMS} items at once. Please limit your selection further.`, 
          { variant: 'error' }
        );
        return;
      }
      
      setItemLimitError(false);
      setMediaItems(initialItems);
      if (!selectedItemId && initialItems.length > 0) {
        setSelectedItemId(String(initialItems[0].id));
      }
      
      // Store initial state for change tracking
      const initialState: Record<string, MediaItemDetailed> = {};
      initialItems.forEach(item => {
        initialState[item.id] = { ...item };
      });
      setInitialItemState(initialState);
    }
  }, [initialItems, enqueueSnackbar]);
  
  // If item IDs are provided, fetch those items
  useEffect(() => {
    if (itemIds.length > 0 && mediaItems.length === 0) {
      // Check if we exceed the bulk edit limit
      if (itemIds.length > MAX_BULK_EDIT_ITEMS) {
        setItemLimitError(true);
        setMediaItems([]);
        enqueueUniqueSnackbar(
          enqueueSnackbar,
          `Cannot bulk edit more than ${MAX_BULK_EDIT_ITEMS} items at once. Please limit your selection further.`, 
          { variant: 'error' }
        );
        return;
      }
      
      setItemLimitError(false);
      console.log(`BulkEditPage: Fetching details for ${itemIds.length} media items with IDs:`, itemIds);
      
      // Directly use the service function
      startLoading();
      fetchMediaItemsService(itemIds)
        .then(allItems => {
          console.log("BulkEditPage: Received items from service:", allItems.length, allItems);
          setMediaItems(allItems);
          
          // Store initial state for change tracking
          const initialState: Record<string, MediaItemDetailed> = {};
          allItems.forEach(item => {
            initialState[item.id] = { ...item };
          });
          setInitialItemState(initialState);
          
          // Select the first item if available
          if (allItems.length > 0) {
            setSelectedItemId(String(allItems[0].id));
            console.log("Selected first item:", allItems[0].id);
          }
        })
        .catch(error => {
          console.error('Error fetching media items by IDs:', error);
          enqueueSnackbar('Failed to load media items', { variant: 'error' });
        })
        .finally(() => {
          stopLoading();
        });
    }
  }, [itemIds, enqueueSnackbar]);

  // Add a useEffect to monitor mediaItems changes
  useEffect(() => {
    console.log("BulkEditPage: mediaItems updated:", mediaItems.length, mediaItems);
    console.log("BulkEditPage: render state:", { 
      saveInProgress, 
      showDropZone, 
      isPreviewEnabled, 
      selectedItemId 
    });
  }, [mediaItems, saveInProgress, showDropZone]);

  useEffect(() => {
    if (selectedItemId && !isPreviewEnabled) {
      setIsPreviewEnabled(true);
    }
  }, [selectedItemId]);

  const handlePreviewClick = (previewData: {
    mediaUrl: string;
    mediaType?: string;
    thumbnail?: string;
  }) => {
    const item = mediaItems.find(
      (item) => item.url === previewData.mediaUrl
    );

    if (item) {
      setPreviewItem(item);
      setPreviewDialogOpen(true);
    }
  };

  const handleFilesDropped = async (files: File[]) => {
    // Implementation for handling file drops if we want to allow 
    // adding new files to the edit session
    enqueueSnackbar("Adding new files in edit mode is not yet implemented", { variant: "info" });
  };

  // Update the handleItemUpdate function to track changes
  const handleItemUpdate = (updates: Partial<MediaItemDetailed>) => {
    try {
      startLoading();
      setMediaItems((prev) => {
        const newItems = prev.map((item) => {
          if (item.id === updates.id) {
            const updatedItem = { ...item, ...updates };
            
            // Track that this item has changed
            setChangedItems(prev => new Set(prev).add(String(updates.id)));
            
            return updatedItem;
          }
          return item;
        });
        return newItems;
      });
    } catch (error) {
      enqueueSnackbar("Error updating item", { variant: "error" });
    } finally {
      stopLoading();
    }
  };

  const handleBulkUpdate = (
    updates: Array<{ id: string; updates: Partial<MediaItemDetailed> }>
  ) => {
    updates.forEach(({ id, updates: itemUpdates }) => {
      handleItemUpdate({ id, ...itemUpdates });
    });
  };

  // Updated save changes function
  const handleSaveChanges = async () => {
    try {
      // Get only the items that have changed
      const itemsToUpdate = mediaItems.filter(item => 
        changedItems.has(String(item.id))
      );
      
      if (itemsToUpdate.length === 0) {
        enqueueSnackbar("No changes to save", { variant: "info" });
        return;
      }
      
      // Set items to save in state
      setItemsToSave(itemsToUpdate);
      
      // Based on the feature flag, either show dialog or proceed directly
      if (SHOW_RELATIONSHIP_PREVIEW_DIALOG) {
        // Show the preview dialog
        setShowDiffPreview(true);
        // Dialog will call executeSave after confirmation
      } else {
        // Skip dialog and proceed directly to save
        console.log("Relationship preview dialog disabled, proceeding directly to save");
        executeSave();
      }
    } catch (error: any) {
      setErrorDetails({
        message: error.message || "Error saving changes",
        failedItems: []
      });
      enqueueSnackbar(error.message || "Unknown error", { variant: "error" });
    }
  };

  // Moved the executeSave function here
  const executeSave = async () => {
    try {
      // Initialize progress tracking
      setSaveInProgress(true);
      setCurrentPhase("uploading");
      setShowDiffPreview(false);
      
      const newProgress: Record<string, FileProgress> = {};
      
      // Initialize progress for each item
      itemsToSave.forEach(item => {
        newProgress[item.id] = {
          stage: 'pending' as FileProgressStage,
          completed: 0,
          total: 1,
          progress: 0
        };
      });
      
      setSaveProgress(newProgress);
      
      // Process each item sequentially
      const failedItems = [];
      
      for (const item of itemsToSave) {
        const itemId = String(item.id);
        
        try {
          // Update progress to show we're processing this item
          setSaveProgress(prev => ({
            ...prev,
            [itemId]: {
              ...prev[itemId],
              progress: 30,
              stage: 'processing'
            }
          }));
          
          // Update progress to show we're sending the request
          setSaveProgress(prev => ({
            ...prev,
            [itemId]: {
              ...prev[itemId],
              progress: 60,
              stage: 'uploading'
            }
          }));
          
          // Use the service to update the media item
          const updatedItem = await updateMediaItem(item, config);
          
          // Update progress to show success
          setSaveProgress(prev => ({
            ...prev,
            [itemId]: {
              ...prev[itemId],
              progress: 100,
              completed: 1,
              stage: 'server'
            }
          }));
          
          // Update the item in our local state
          setMediaItems(prev => 
            prev.map(prevItem => 
              prevItem.id === itemId ? updatedItem : prevItem
            )
          );
          
        } catch (error) {
          console.error(`Error saving item ${itemId}:`, error);
          
          // Update progress to show error
          setSaveProgress(prev => ({
            ...prev,
            [itemId]: {
              ...prev[itemId],
              progress: 0,
              stage: 'pending'
            }
          }));
          
          failedItems.push({
            id: itemId,
            error: error instanceof Error ? error.message : 'Unknown error'
          });
        }
      }

      // Handle success or partial failure
      if (failedItems.length > 0) {
        setErrorDetails({
          message: `Failed to save ${failedItems.length} items`,
          failedItems
        });
        enqueueSnackbar(`${itemsToSave.length - failedItems.length} saved, ${failedItems.length} failed`, { variant: "warning" });
      } else {
        // All successful
        setSaveComplete(true);
        setChangedItems(new Set()); // Clear the changed items tracker
        enqueueSnackbar(`Successfully saved ${itemsToSave.length} items`, { variant: "success" });
        
        // Refresh all updated items after a short delay
        setTimeout(async () => {
          const itemIdsToRefresh = itemsToSave.map(item => String(item.id));
          console.log("Re-fetching all updated items to refresh data");
          // Directly use the service function
          await fetchMediaItemsService(itemIdsToRefresh)
            .then(refreshedItems => {
              setMediaItems(prev => {
                // Replace updated items with refreshed data
                const itemMap = new Map(prev.map(item => [item.id, item]));
                refreshedItems.forEach(item => {
                  itemMap.set(item.id, item);
                });
                return Array.from(itemMap.values());
              });
            })
            .catch(error => {
              console.error('Error refreshing items:', error);
            });
        }, 1000);
      }
      
    } catch (error: any) {
      setErrorDetails({
        message: error.message || "Error saving changes",
        failedItems: []
      });
      enqueueSnackbar(error.message || "Unknown error", { variant: "error" });
    } finally {
      // After a short delay to show completion, reset the UI
      setTimeout(() => {
        setSaveInProgress(false);
        setCurrentPhase("importing");
      }, 2000);
    }
  };

  const handleFinishConfirmation = () => {
    setSaveComplete(false);
    if (onSaveComplete) {
      onSaveComplete();
    }
  };

  const handleDeleteItem = async (id: string) => {
    try {
      // Clear selection if deleting selected item
      if (selectedItemId === id) {
        setSelectedItemId(undefined);
      }

      // Clear preview if showing deleted item
      if (previewItem?.id === id) {
        setPreviewItem(null);
        setPreviewDialogOpen(false);
      }

      // Allow state updates to propagate
      await new Promise(resolve => setTimeout(resolve, 50));

      // In a real implementation, we might queue this item for deletion on save
      // or remove it immediately via API call

      setMediaItems(prevItems => {
        const newItems = prevItems.filter(item => item.id !== id);

        // Update preview state based on remaining items
        if (newItems.length === 0) {
          setIsPreviewEnabled(false);
        } else if (selectedItemId === id) {
          // Select first remaining item
          setSelectedItemId(String(newItems[0].id));
        }

        return newItems;
      });
    } catch (error) {
      console.error('Error deleting item:', error);
      enqueueSnackbar('Error deleting item', { variant: 'error' });
    }
  };

  // Define a type for relationships in our diff viewer
  interface RelationshipEntity {
    id: string | number;
    entity_name?: string;
    name?: string;
    [key: string]: any; // Allow other properties
  }

  // Define a Record for added/removed relationships
  type RelationshipsMap = Record<string, RelationshipEntity[]>;

  // 4. Relationship Diff Visualization Component
  const RelationshipDiffViewer = ({ 
    original, 
    updated 
  }: { 
    original: MediaItemDetailed, 
    updated: MediaItemDetailed 
  }) => {
    const addedRels: RelationshipsMap = {};
    const removedRels: RelationshipsMap = {};
    const changedRels: RelationshipsMap = {};
    const config = useConfig();
    
    // Create key mapping for normalization (similar to transformRelationships)
    const apiToUiKeyMap = React.useMemo(() => {
      // Build a bidirectional mapping to handle both directions
      const mapping: Record<string, string> = {};
      
      if (config && config.apiEndpoints) {
        config.apiEndpoints.forEach((endpoint: any) => {
          if (endpoint.relationshipKey && endpoint.returnedRelationshipsKey) {
            // Map both directions for flexibility
            mapping[endpoint.relationshipKey] = endpoint.returnedRelationshipsKey;
            mapping[endpoint.returnedRelationshipsKey] = endpoint.relationshipKey;
          }
        });
      }
      
      return mapping;
    }, [config]);
    
    // Helper to normalize a relationship key between formats
    const normalizeRelKey = (key: string): string => {
      // Try direct mapping from config
      if (apiToUiKeyMap[key]) {
        return apiToUiKeyMap[key];
      }
      
      // As fallback, convert between formats:
      // snake_case ↔ camelCase
      if (key.includes('_')) {
        // Convert snake_case to camelCase
        return key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      } else {
        // Try to convert camelCase to snake_case
        return key.replace(/([A-Z])/g, '_$1').toLowerCase();
      }
    };
    
    // Helper to get a display-friendly relationship type name
    const getDisplayRelKey = (key: string): string => {
      // Make it more human-readable
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    };
    
    // Debug log the items we're comparing
    console.log("Comparing relationships:", {
      originalId: original.id,
      updatedId: updated.id,
      originalRels: original.relationships,
      updatedRels: updated.relationships
    });
    
    if (!original.relationships && !updated.relationships) {
      return (
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
          No relationships to compare
        </Typography>
      );
    }

    // Type guard to check if relationships exist
    const origRelationships = original.relationships as Record<string, any> | undefined;
    const updatedRelationships = updated.relationships as Record<string, any> | undefined;

    if (!origRelationships && updatedRelationships) {
      // All relationships are new
      Object.entries(updatedRelationships).forEach(([key, values]) => {
        if (!values) return;
        const valuesArray = Array.isArray(values) ? values : [values].filter(Boolean);
        if (valuesArray.length > 0) {
          addedRels[key] = valuesArray.filter(item => item && item.id) as RelationshipEntity[];
        }
      });
    } else if (origRelationships && !updatedRelationships) {
      // All relationships were removed
      Object.entries(origRelationships).forEach(([key, values]) => {
        if (!values) return;
        const valuesArray = Array.isArray(values) ? values : [values].filter(Boolean);
        if (valuesArray.length > 0) {
          removedRels[key] = valuesArray.filter(item => item && item.id) as RelationshipEntity[];
        }
      });
    } else if (origRelationships && updatedRelationships) {
      // Collect all normalized keys from both objects
      const allNormalizedKeys = new Set<string>();
      
      // Extract and normalize keys from both objects
      Object.keys(origRelationships).forEach(key => {
        // For each section (related_entities, geo_entities)
        if (typeof origRelationships[key] === 'object' && origRelationships[key] !== null) {
          if (key === 'related_entities' || key === 'geo_entities') {
            // Process entity collections
            Object.keys(origRelationships[key]).forEach(entityKey => {
              allNormalizedKeys.add(normalizeRelKey(entityKey));
            });
          } else if (key === 'media') {
            // Media is handled differently, just add it directly
            allNormalizedKeys.add('media');
          }
        }
      });
      
      Object.keys(updatedRelationships).forEach(key => {
        // For each section (related_entities, geo_entities)
        if (typeof updatedRelationships[key] === 'object' && updatedRelationships[key] !== null) {
          if (key === 'related_entities' || key === 'geo_entities') {
            // Process entity collections
            Object.keys(updatedRelationships[key]).forEach(entityKey => {
              allNormalizedKeys.add(normalizeRelKey(entityKey));
            });
          } else if (key === 'media') {
            // Media is handled differently, just add it directly
            allNormalizedKeys.add('media');
          }
        }
      });
      
      // For each normalized key, compare values from both objects
      allNormalizedKeys.forEach(normalizedKey => {
        // Find possible variants of this key in both objects
        const possibleKeys = [
          normalizedKey,
          normalizeRelKey(normalizedKey)
        ];
        
        let origValues: any[] = [];
        let updatedValues: any[] = [];
        
        // Try to find values in original
        for (const section of ['related_entities', 'geo_entities']) {
          if (origRelationships[section]) {
            for (const possibleKey of possibleKeys) {
              if (origRelationships[section][possibleKey]) {
                const values = origRelationships[section][possibleKey];
                origValues = Array.isArray(values) ? values : [values];
                break;
              }
            }
          }
        }
        
        // If it's media, handle differently
        if (normalizedKey === 'media' && origRelationships.media) {
          origValues = Array.isArray(origRelationships.media) ? origRelationships.media : [origRelationships.media];
        }
        
        // Try to find values in updated
        for (const section of ['related_entities', 'geo_entities']) {
          if (updatedRelationships[section]) {
            for (const possibleKey of possibleKeys) {
              if (updatedRelationships[section][possibleKey]) {
                const values = updatedRelationships[section][possibleKey];
                updatedValues = Array.isArray(values) ? values : [values];
                break;
              }
            }
          }
        }
        
        // If it's media, handle differently
        if (normalizedKey === 'media' && updatedRelationships.media) {
          updatedValues = Array.isArray(updatedRelationships.media) ? updatedRelationships.media : [updatedRelationships.media];
        }
        
        // Filter out items without id and cast to proper type
        const safeOrigArray = origValues.filter(item => item && item.id) as RelationshipEntity[];
        const safeUpdatedArray = updatedValues.filter(item => item && item.id) as RelationshipEntity[];
        
        // Make maps for faster lookup
        const origMap = new Map(safeOrigArray.map(r => [String(r.id), r]));
        const updatedMap = new Map(safeUpdatedArray.map(r => [String(r.id), r]));
        
        // Find added (in updated but not in original)
        const added = safeUpdatedArray.filter(r => !origMap.has(String(r.id)));
        if (added.length > 0) addedRels[normalizedKey] = added;
        
        // Find removed (in original but not in updated)
        const removed = safeOrigArray.filter(r => !updatedMap.has(String(r.id)));
        if (removed.length > 0) removedRels[normalizedKey] = removed;
        
        // Find items with changed properties
        safeOrigArray.forEach(origRel => {
          const updatedRel = updatedMap.get(String(origRel.id));
          if (updatedRel && !areRelationshipsEqual(origRel, updatedRel)) {
            if (!changedRels[normalizedKey]) changedRels[normalizedKey] = [];
            changedRels[normalizedKey].push({
              ...updatedRel,
              _original: origRel // Store original for displaying the change
            });
          }
        });
      });
    }
    
    // Check if there are any changes
    const hasChanges = 
      Object.values(addedRels).some(arr => arr.length > 0) || 
      Object.values(removedRels).some(arr => arr.length > 0) ||
      Object.values(changedRels).some(arr => arr.length > 0);
    
    if (!hasChanges) {
      // Add additional debug information when no changes are detected
      console.log("No relationship changes detected for:", {
        itemId: updated.id,
        itemName: updated.name,
        originalJSON: JSON.stringify(original.relationships),
        updatedJSON: JSON.stringify(updated.relationships),
        isEqual: JSON.stringify(original.relationships) === JSON.stringify(updated.relationships)
      });
      
      return (
        <Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            No relationship changes detected
          </Typography>
          {JSON.stringify(original.relationships) !== JSON.stringify(updated.relationships) && (
            <Alert severity="warning" sx={{ mt: 1 }}>
              Internal data has changed but could not be displayed in readable format
            </Alert>
          )}
        </Box>
      );
    }
    
    return (
      <Paper elevation={0} sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, mb: 2 }}>
        <Typography variant="h6" gutterBottom>Changes Summary</Typography>
        
        {/* Added Relationships */}
        {Object.keys(addedRels).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'success.main', mb: 1 }}>
              Adding:
            </Typography>
            {Object.entries(addedRels).map(([key, entities]) => (
              <Box key={`added-${key}`} sx={{ ml: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {getDisplayRelKey(key)}:
                </Typography>
                <List dense disablePadding>
                  {entities.map((rel) => (
                    <ListItem key={`add-${rel.id}`} dense sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`+ ${getEntityName(rel)}`} 
                        primaryTypographyProps={{ color: 'success.main' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}
        
        {/* Removed Relationships */}
        {Object.keys(removedRels).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'error.main', mb: 1 }}>
              Removing:
            </Typography>
            {Object.entries(removedRels).map(([key, entities]) => (
              <Box key={`removed-${key}`} sx={{ ml: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {getDisplayRelKey(key)}:
                </Typography>
                <List dense disablePadding>
                  {entities.map((rel) => (
                    <ListItem key={`remove-${rel.id}`} dense sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`- ${getEntityName(rel)}`} 
                        primaryTypographyProps={{ color: 'error.main' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}
        
        {/* Changed Relationship Properties */}
        {Object.keys(changedRels).length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ color: 'info.main', mb: 1 }}>
              Modifying:
            </Typography>
            {Object.entries(changedRels).map(([key, entities]) => (
              <Box key={`changed-${key}`} sx={{ ml: 2, mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                  {getDisplayRelKey(key)}:
                </Typography>
                <List dense disablePadding>
                  {entities.map((rel: any) => (
                    <ListItem key={`change-${rel.id}`} dense sx={{ py: 0.5 }}>
                      <ListItemText 
                        primary={`~ ${getEntityName(rel)}`} 
                        secondary={getChangeSummary(rel._original, rel)}
                        primaryTypographyProps={{ color: 'info.main' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ))}
          </Box>
        )}
      </Paper>
    );
  };

  // Helper functions for the RelationshipDiffViewer
  const getEntityName = (entity: RelationshipEntity): string => {
    return entity.entity_name || entity.name || `Item ${entity.id}`;
  };

  const areRelationshipsEqual = (a: any, b: any): boolean => {
    if (!a || !b) return a === b;
    
    // Compare properties that matter for relationships
    return (
      String(a.id) === String(b.id) &&
      a.entity_name === b.entity_name &&
      a.name === b.name &&
      a.is_primary === b.is_primary
    );
  };

  const getChangeSummary = (original: any, updated: any): string => {
    const changes: string[] = [];
    
    if (original.entity_name !== updated.entity_name) {
      changes.push(`Name: "${original.entity_name || ''}" → "${updated.entity_name || ''}"`);
    }
    
    if (original.name !== updated.name && (original.name || updated.name)) {
      changes.push(`Name: "${original.name || ''}" → "${updated.name || ''}"`);
    }
    
    if (original.is_primary !== updated.is_primary) {
      changes.push(`Primary: ${original.is_primary ? 'Yes' : 'No'} → ${updated.is_primary ? 'Yes' : 'No'}`);
    }
    
    return changes.length > 0 ? changes.join(', ') : 'Properties changed';
  };

  // Check permissions early - if user can't edit, show permission message
  if (!hasWritePermissions()) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100vh",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
          textAlign: "center",
        }}
      >
        <Typography variant="h5" color="text.secondary" gutterBottom>
          Edit Access Required
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
          You need edit permissions to access this feature. Please contact your administrator if you believe you should have edit access.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        p: "2em",
        gap: "2em",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Box 
        sx={{ 
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {saveInProgress ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <UploadProgressBars progress={saveProgress} items={mediaItems} />
          </Box>
        ) : itemLimitError ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              textAlign: "center",
              p: 4,
            }}
          >
            <Alert severity="error" sx={{ mb: 2, maxWidth: 600 }}>
              <Typography variant="h6" gutterBottom>
                Too Many Items Selected
              </Typography>
              <Typography variant="body2">
                Bulk editing is limited to {MAX_BULK_EDIT_ITEMS} items at once for performance reasons.
                Please use the filters to limit your selection further, or edit items in smaller batches.
              </Typography>
            </Alert>
            {onCancel && (
              <CMButton
                onClick={onCancel}
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
              >
                Go Back
              </CMButton>
            )}
          </Box>
        ) : (
          <>
            <Collapse
              in={isPreviewEnabled && selectedItemId !== undefined}
              collapsedSize={0}
            >
              <Box sx={{ height: "30vh", position: "relative" }}>
                {selectedItemId && (
                  <SingleItemPreview
                    item={
                      mediaItems.find((item) => item.id === selectedItemId)!
                    }
                    onUpdate={handleItemUpdate}
                    onPreviewClick={(url) => {
                      if (url) {
                        setPreviewItem(
                          mediaItems.find(
                            (item) => item.id === selectedItemId
                          )!
                        );
                        setPreviewDialogOpen(true);
                      }
                    }}
                  />
                )}
              </Box>
            </Collapse>

            {selectedItemId && (
              <ToggleButton
                isOpen={isPreviewEnabled}
                onClick={() => setIsPreviewEnabled(!isPreviewEnabled)}
                direction="vertical"
                position={{
                  left: "50%",
                  transform: "translateX(-50%)",
                  top: isPreviewEnabled ? "28vh" : "-1vh",
                }}
              />
            )}

            <Box 
              sx={{ 
                flex: 1,
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                position: "relative",
              }}
            >
              <Card 
                elevation={3} 
                sx={{ 
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <UploadTableContainer
                  items={mediaItems}
                  onItemSelect={setSelectedItemId}
                  selectedItemId={selectedItemId}
                  onPreviewClick={handlePreviewClick}
                  onItemUpdate={handleItemUpdate}
                  onBulkUpdate={handleBulkUpdate}
                  onFilesDropped={handleFilesDropped}
                  onDeleteItem={handleDeleteItem}
                  mode="edit"
                />
              </Card>

              {mediaItems.length > 0 && (
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 16,
                    left: 16,
                    zIndex: 2,
                    display: "flex",
                    gap: 2,
                  }}
                >
                  <CMButton
                    onClick={handleSaveChanges}
                    variant="contained"
                    color="primary"
                    disabled={saveInProgress}
                    startIcon={<SaveIcon />}
                  >
                    Save Changes
                  </CMButton>
                  {onCancel && (
                    <CMButton
                      onClick={onCancel}
                      variant="outlined"
                      color="primary"
                      disabled={saveInProgress}
                    >
                      Cancel
                    </CMButton>
                  )}
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={saveComplete}
        onClose={() => {}}
        PaperProps={{
          sx: {
            minWidth: "auto",
            maxWidth: "400px",
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            m: 0,
            p: 2,
            borderRadius: 2,
            boxShadow: 24,
          },
        }}
      >
        <DialogContent sx={{ textAlign: "center", py: 3 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2,
              alignItems: "center",
            }}
          >
            <Typography variant="h6">Save Complete! 🎉</Typography>
            <Typography variant="body2" color="text.secondary">
              All changes have been successfully saved.
            </Typography>
            <CMButton
              onClick={handleFinishConfirmation}
              variant="contained"
              color="primary"
              sx={{ mt: 1 }}
            >
              OK
            </CMButton>
          </Box>
        </DialogContent>
      </Dialog>

      {previewItem && (
        <PreviewDialog
          open={previewDialogOpen}
          onClose={() => setPreviewDialogOpen(false)}
          file={previewItem.file ?? null}
          fileType={previewItem.media_type ?? previewItem.type}
          url={previewItem.url}
        />
      )}

      {/* Save Preview Dialog */}
      <Dialog
        open={showDiffPreview}
        onClose={() => setShowDiffPreview(false)}
        maxWidth="md"
        PaperProps={{
          sx: {
            minWidth: "500px",
            maxWidth: "800px",
            maxHeight: "80vh"
          }
        }}
      >
        <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h5" gutterBottom>
              Review Changes
            </Typography>
            <Typography variant="body2" color="text.secondary" paragraph>
              Please review the relationship changes before saving. These changes will update how items are connected in the database.
            </Typography>
            
            <Alert severity="info" sx={{ mb: 2 }}>
              You are about to save changes to {itemsToSave.length} item{itemsToSave.length !== 1 ? 's' : ''}.
            </Alert>
          </Box>
          
          <Box sx={{ maxHeight: '50vh', overflow: 'auto', px: 1 }}>
            {itemsToSave.map((item, index) => (
              <Box key={item.id} sx={{ mb: 3 }}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {index + 1}. {item.name || `Item ${item.id}`}
                </Typography>
                <RelationshipDiffViewer 
                  original={initialItemState[item.id] || item} 
                  updated={item} 
                />
                {index < itemsToSave.length - 1 && <Divider sx={{ my: 2 }} />}
              </Box>
            ))}
          </Box>
          
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <CMButton
              onClick={() => setShowDiffPreview(false)}
              variant="outlined"
            >
              Cancel
            </CMButton>
            <CMButton
              onClick={executeSave}
              variant="contained"
              color="primary"
            >
              Confirm & Save
            </CMButton>
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default BulkEditPage; 